import { useEffect, useState } from 'react'
import { adminApi, ApiError, type AdminOrganization } from '../lib/api'

export function Details({
  org,
  onSaved,
}: {
  org: AdminOrganization
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    name: org.name,
    timezone: org.timezone,
    default_region: org.default_region ?? 'CA',
    dispatch_tenant_id: org.dispatch_tenant_id ?? '',
    fleet_network: org.fleet_network ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setForm({
      name: org.name,
      timezone: org.timezone,
      default_region: org.default_region ?? 'CA',
      dispatch_tenant_id: org.dispatch_tenant_id ?? '',
      fleet_network: org.fleet_network ?? '',
    })
    setSaved(false)
  }, [org.id, org.name, org.timezone, org.default_region,
      org.dispatch_tenant_id, org.fleet_network])

  async function save(patch: Record<string, unknown>) {
    setBusy(true)
    setSaved(false)
    try {
      await adminApi.organizationUpdate(org.id, patch)
      setSaved(true)
      setError(null)
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="pad">
      <h2>{org.name}</h2>

      {error && <div className="notice">{error}</div>}

      <div className="summary">
        <div>
          <span className="k">Address</span>
          <a href={`https://${org.slug}.crm.advancetalk.ca`} target="_blank"
             rel="noreferrer" className="mono">
            https://{org.slug}.crm.advancetalk.ca
          </a>
        </div>
        <div><span className="k">Short name</span><span className="mono">{org.slug}</span></div>
        <div>
          <span className="k">Status</span>
          {org.active ? 'Active' : 'Inactive — their address stops working'}
        </div>
      </div>

      <p className="footnote">
        The short name cannot be changed. It is the hostname their certificate
        was issued against, so renaming it would strand the certificate and
        break every bookmark. Moving a customer means a new organization and a
        deliberate migration.
      </p>

      <div className="grid2">
        <div className="field">
          <label>Organization name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Time zone — display only, everything is stored in UTC</label>
          <input
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Country — how a phone number with no country code is read</label>
          <input
            value={form.default_region}
            maxLength={2}
            placeholder="CA"
            style={{ textTransform: 'uppercase' }}
            onChange={(e) =>
              setForm({ ...form, default_region: e.target.value.toUpperCase() })}
          />
          {/* This is not cosmetic. A number typed `514 555 1234` is Montreal
              to a Montreal dispatcher and nothing at all to a London one, and
              getting it wrong does not fail loudly: a ten-digit foreign number
              IS a possible North American number, so it is silently read as
              one and the text goes to a stranger. A number entered WITH its
              country code ignores this entirely. */}
          <p className="hint">
            Two letters, e.g. <code>CA</code>, <code>US</code>,{' '}
            <code>GB</code>, <code>IL</code>. Only used for numbers typed
            without a leading <code>+</code> — anything with a country code is
            read as written. Get this wrong and a foreign number is not
            rejected, it is silently read as a local one.
          </p>
        </div>
      </div>

      <h3 className="sub">Links to the systems alongside the CRM</h3>
      <p className="blurb">
        Kept as plain identifiers on purpose. The CRM owns its own
        organizations and logins — if it depended on the provisioning server
        for those, an outage there would lock dispatchers out of their own CRM.
      </p>

      <div className="grid2">
        <div className="field">
          <label>Dispatch tenant id</label>
          <input
            value={form.dispatch_tenant_id}
            placeholder="t_abc"
            onChange={(e) => setForm({ ...form, dispatch_tenant_id: e.target.value })}
          />
        </div>
        <div className="field">
          <label>WalkieFleet network</label>
          <input
            value={form.fleet_network}
            onChange={(e) => setForm({ ...form, fleet_network: e.target.value })}
          />
        </div>
      </div>

      <div className="btnrow">
        <button className="primary compact" disabled={busy} onClick={() => save(form)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          className="ghost"
          onClick={() => save({ active: !org.active })}
        >
          {org.active ? 'Deactivate customer' : 'Reactivate customer'}
        </button>
        {saved && <span className="testresult ok">Saved.</span>}
      </div>

      {org.active && (
        <p className="footnote">
          Deactivating stops their address resolving to a certificate and signs
          out every dispatcher. Their data is untouched and reactivating brings
          it straight back.
        </p>
      )}
    </div>
  )
}
